'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';

type MadeWithLoveProps = {
  locations?: string[];
  scrambleSpeed?: number;
  pauseDuration?: number;
  className?: string;
};

const MadeWithLove: React.FC<MadeWithLoveProps> = ({
  locations = [
    'California, USA',
    'Florida, USA',
    'Kentucky, USA',
    'Vermont, USA',
  ],
  scrambleSpeed = 100,
  pauseDuration = 2000,
  className = '',
}) => {
  const [displayText, setDisplayText] = useState<string>(locations[0]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isScrambling, setIsScrambling] = useState<boolean>(false);
  const [maxWidth, setMaxWidth] = useState<number>(0);
  const measureRef = useRef<HTMLSpanElement>(null);

  const scrambleChars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=[]{}|;:',.<>?/💅🔥";

  useEffect(() => {
    if (measureRef.current) {
      let max = 0;
      locations.forEach((location) => {
        measureRef.current!.textContent = location;
        max = Math.max(max, measureRef.current!.offsetWidth + 10);
      });
      setMaxWidth(max);
    }
  }, [locations]);

  const animateText = useCallback(
    (targetText: string) => {
      setIsScrambling(true);
      let iterations = 0;
      const targetLength = targetText.length;

      const scrambleInterval = setInterval(() => {
        setDisplayText(
          targetText
            .split('')
            .map((_, idx) => {
              if (idx < iterations) return targetText[idx];
              return scrambleChars[
                Math.floor(Math.random() * scrambleChars.length)
              ];
            })
            .join('')
        );

        if (iterations >= targetLength) {
          clearInterval(scrambleInterval);
          setIsScrambling(false);
        }
        iterations++;
      }, scrambleSpeed);

      return () => clearInterval(scrambleInterval);
    },
    [scrambleSpeed]
  );

  // Rotate locations with a pause
  useEffect(() => {
    if (isScrambling || maxWidth === 0) return;

    const pauseTimeout = setTimeout(() => {
      const nextIndex = (currentIndex + 1) % locations.length;
      setCurrentIndex(nextIndex);
      animateText(locations[nextIndex]);
    }, pauseDuration);

    return () => clearTimeout(pauseTimeout);
  }, [
    currentIndex,
    isScrambling,
    locations,
    pauseDuration,
    animateText,
    maxWidth,
  ]);

  // Kick off the first animation
  useEffect(() => {
    if (maxWidth > 0) animateText(locations[0]);
  }, [maxWidth]); // eslint-disable-line

  return (
    <div className={`p-5 text-center font-sans ${className}`}>
      {/* Hidden span to measure text width */}
      <span
        ref={measureRef}
        aria-hidden
        className='pointer-events-none absolute whitespace-nowrap font-bold opacity-0'
      />

      {/* ACTUAL FOOTER CONTENT */}
      <div className='inline-flex items-center justify-center whitespace-nowrap'>
        <span className='whitespace-nowrap'>
          Made with <span className='text-red-500'>❤️</span> in{' '}
        </span>
        <span
          className='inline-block whitespace-nowrap pl-1 text-left font-bold text-purple-500'
          style={{ width: `${maxWidth}px` }}
        >
          {displayText}
        </span>
      </div>
    </div>
  );
};

export default MadeWithLove;
